import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import NavigationBar from '../NavigationBar';
import ThemedBackground from './ThemedBackground';
import GameHeader from './GameHeader';
import Celebration, { CELEBRATION_TYPES } from './Celebration';
import MobileGameplayBanner from './MobileGameplayBanner';
import ShellMainWithStickyAdRail from './ShellMainWithStickyAdRail';
import { useGlobalModal } from '../../contexts/ModalContext';
import { useAIHelper } from '../../contexts/AIHelperContext';
import { useGameStartAccountOffer } from '../../contexts/GameStartAccountOfferContext';
import { GameResultsPresentationContext } from '../../contexts/GameResultsPresentationContext';
import { config } from '../../config';
import styles from '../../styles/layout/GameShell.module.css';

const SIDE_PANEL_WIDTH_PX = 380;

/**
 * GameShell - Layout wrapper for game pages
 * Provides themed background, game header with actions, and content wrapper.
 * 
 * Modal functionality is provided globally via ModalContext.
 * 
 * @param {Object} gameConfig - Game configuration from games.js
 * @param {Array} actions - Header action buttons [{ icon, title, onClick, disabled }]
 * @param {React.ReactNode} children - Game content
 * @param {boolean} isModalOpen - Whether the game-specific modal is open
 * @param {function} onModalClose - Modal close handler
 * @param {string} modalTitle - Modal title
 * @param {React.ReactNode} modalContent - Modal content
 * @param {boolean} showCelebration - Trigger win celebration animation (one-time)
 * @param {boolean} showNavigation - Whether to render the top navigation bar
 * @param {boolean} showGameHeader - Whether to render the game header
 * @param {boolean} showHeaderShareAction - Whether to render the share action in the game header
 * @param {boolean} showStickyAdRail - Whether to render the shared sticky right ad rail
 * @param {boolean} isCompleted - Whether the game is in a terminal won/lost state
 * @param {Object|null} mobileGameplayAd - Mobile gameplay banner configuration
 * @param {boolean} isLoading - Whether puzzle access is still being checked
 */
const GameShell = ({ 
  gameConfig,
  actions = [],
  children,
  sponsorship = null,
  isModalOpen = false,
  onModalClose,
  modalTitle,
  modalContent,
  showCelebration = false,
  showNavigation = true,
  showGameHeader = true,
  showHeaderShareAction = true,
  showStickyAdRail = true,
  isCompleted = false,
  resultsPresentationReady = true,
  mobileGameplayAd = null,
  isLoading = false,
}) => {
  const hasThemedBackground = !!gameConfig?.backgroundColor;
  const { openModal, closeModal, isOpen: isGlobalModalOpen } = useGlobalModal();
  const { isOpen: isAIPanelOpen } = useAIHelper();
  const { cancelPendingOffer, setGamePresentationPending, setGameplayActive } = useGameStartAccountOffer();
  const wasOpenRef = useRef(false);
  const onModalCloseRef = useRef(onModalClose);

  const handlePuzzleKey = useCallback((event) => {
    if (event.ctrlKey || event.metaKey || event.altKey) return;
    if (event.key?.length === 1 || ['Backspace', 'Delete', 'Enter', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.key)) {
      cancelPendingOffer();
    }
  }, [cancelPendingOffer]);

  // Ortografia and Scalinata also accept typing while the body has focus.
  useEffect(() => {
    const handleBodyKey = (event) => {
      if (!isGlobalModalOpen && event.target === document.body) handlePuzzleKey(event);
    };
    window.addEventListener('keydown', handleBodyKey);
    return () => window.removeEventListener('keydown', handleBodyKey);
  }, [isGlobalModalOpen, handlePuzzleKey]);
  
  // Track if celebration animation has completed (to remove from DOM after animation)
  const [celebrationComplete, setCelebrationComplete] = useState(false);
  const [coachmarkVersion, setCoachmarkVersion] = useState(0);
  const requestCoachmark = useCallback(() => setCoachmarkVersion((version) => version + 1), []);
  const completeCelebration = useCallback(() => setCelebrationComplete(true), []);
  const celebrationPending = showCelebration && !celebrationComplete;
  useEffect(() => {
    setGameplayActive?.(!isCompleted);
    return () => setGameplayActive?.(false);
  }, [isCompleted, setGameplayActive]);
  const presentation = useMemo(() => ({ celebrationPending, boardPresentationReady: resultsPresentationReady, coachmarkVersion, requestCoachmark }), [celebrationPending, resultsPresentationReady, coachmarkVersion, requestCoachmark]);
  useEffect(() => {
    setGamePresentationPending?.(isCompleted && (!resultsPresentationReady || celebrationPending));
    return () => setGamePresentationPending?.(false);
  }, [isCompleted, resultsPresentationReady, celebrationPending, setGamePresentationPending]);
  
  // Dev-only: state for testing celebrations (key forces fresh component instance)
  const [devCelebration, setDevCelebration] = useState({ active: false, type: null, key: 0 });
  const devTypeIndexRef = useRef(0);
  
  // Dev-only: Ctrl+Alt+C to cycle through celebration animations
  const handleDevCelebration = useCallback(() => {
    const type = CELEBRATION_TYPES[devTypeIndexRef.current];
    devTypeIndexRef.current = (devTypeIndexRef.current + 1) % CELEBRATION_TYPES.length;
    setDevCelebration(prev => ({ active: true, type, key: prev.key + 1 }));
    console.log(`[Dev] Testing celebration: ${type}`);
  }, []);
  
  useEffect(() => {
    onModalCloseRef.current = onModalClose;
  }, [onModalClose]);

  useEffect(() => {
    if (config.ENVIRONMENT !== 'development') return;
    
    const handleKeyPress = (e) => {
      // Ctrl+Alt+C: Test celebration animations (cycles through types)
      if (e.key === 'c' && e.altKey && e.ctrlKey) {
        e.preventDefault();
        handleDevCelebration();
      }
    };
    
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [handleDevCelebration]);

  // Sync local isModalOpen state with global modal
  useEffect(() => {
    if (isModalOpen && !wasOpenRef.current) {
      // Opening: show modal via global context
      openModal({
        alignToGame: true,
        title: modalTitle,
        content: modalContent,
        onClose: onModalCloseRef.current,
        analytics: {
          source_surface: 'game_shell',
          modal_name: modalTitle || null,
          game_slug: gameConfig?.id || null,
          trigger: 'state_sync_open',
        },
      });
      wasOpenRef.current = true;
    } else if (!isModalOpen && wasOpenRef.current) {
      // Closing: close global modal
      closeModal();
      wasOpenRef.current = false;
    }
  }, [isModalOpen, modalTitle, modalContent, openModal, closeModal]);

  // Update modal content if it changes while open
  useEffect(() => {
    if (isModalOpen && wasOpenRef.current) {
      openModal({
        alignToGame: true,
        title: modalTitle,
        content: modalContent,
        onClose: onModalCloseRef.current,
        analytics: {
          source_surface: 'game_shell',
          modal_name: modalTitle || null,
          game_slug: gameConfig?.id || null,
          trigger: 'state_sync_refresh',
        },
      });
    }
  }, [isModalOpen, modalTitle, modalContent, openModal]);

  // Clean up: close modal when component unmounts
  useEffect(() => {
    return () => {
      if (wasOpenRef.current) {
        closeModal();
        wasOpenRef.current = false;
      }
    };
  }, [closeModal]);

  return (
    <GameResultsPresentationContext.Provider value={presentation}>
      {/* Fixed Navigation Bar */}
      {showNavigation && (
        <div data-google-interstitial="false">
          <NavigationBar />
        </div>
      )}

      {/* Main content area */}
      <ShellMainWithStickyAdRail
        as="main"
        className={`${styles.content} ${hasThemedBackground ? styles.themedContent : ''} ${
          !showNavigation ? styles.noNav : ''
        } ${isAIPanelOpen ? styles.aiPanelOpen : ''}`}
        showStickyAdRail={showStickyAdRail}
        railPlacementBase={`game-shell-${gameConfig?.id || 'game'}-sticky-rail`}
        testId="game-shell-main-layout"
      >
        {/* Themed background for game pages */}
        {hasThemedBackground && (
          <ThemedBackground backgroundColor={gameConfig.backgroundColor} />
        )}

        <div className={styles.shellLayout}>
          <div className={styles.centerColumn}>
            {/* Central content wrapper - white card on wider screens */}
            <div className={hasThemedBackground ? styles.contentWrapper : undefined}>
              {/* Win celebration animation - showCelebration should be true only when user just won */}
              {showCelebration && !celebrationComplete && (
                <Celebration 
                  trigger={showCelebration} 
                  onComplete={completeCelebration}
                />
              )}
              
              {/* Dev-only: Test celebration triggered by Ctrl+Alt+C */}
              {devCelebration.active && (
                <Celebration 
                  key={`dev-celebration-${devCelebration.key}`}
                  trigger={true}
                  type={devCelebration.type}
                  onComplete={() => setDevCelebration(prev => ({ ...prev, active: false }))} 
                />
              )}
              
              {/* Game header with title and action icons */}
              {showGameHeader && (
                <div data-google-interstitial="false">
                  <GameHeader 
                    title={gameConfig?.displayName}
                    icon={gameConfig?.icon}
                    gameConfig={gameConfig}
                    actions={actions}
                    showShareAction={showHeaderShareAction}
                    sponsorship={sponsorship}
                    isLoading={isLoading}
                  />
                </div>
              )}
              
              {/* Game-specific content */}
              <div
                style={{ display: 'contents' }}
                data-gameplay-interactions
                onPointerDownCapture={cancelPendingOffer}
                onClickCapture={cancelPendingOffer}
                onKeyDownCapture={handlePuzzleKey}
                onInputCapture={cancelPendingOffer}
              >
                {children}
              </div>
            </div>
          </div>
        </div>

        {mobileGameplayAd ? (
          <div data-google-interstitial="false">
            <MobileGameplayBanner
              enabled={mobileGameplayAd.enabled}
              anchors={mobileGameplayAd.anchors}
              placement={mobileGameplayAd.placement}
              testId={mobileGameplayAd.testId}
            />
          </div>
        ) : null}
      </ShellMainWithStickyAdRail>
    </GameResultsPresentationContext.Provider>
  );
};

export default GameShell;
